// Configuration for rich content tags in AI responses
export type RichContentTagType =
  | "FOLLOWUP"
  | "BUTTON"
  | "SCHEDULING"
  | "CALENDLY"
  | "IFRAME"
  | "LINK"
  | "HYPERLINK";

export type RichContentLocation = "inside" | "below" | "bottom";

export type RichContentTag = {
  regex: RegExp;
  location: RichContentLocation;
  component: string;
  attributeRegex?: RegExp;
  requiredAttributes?: string[];
  allowedDomains?: string[];
  description?: string;
};

// Type for extracted rich content items
export type ExtractedContent = {
  type: RichContentTagType;
  content: string;
  attributes?: Record<string, string>;
  rawMatch?: string;
};

// Configuration for rich content tags
export const RICH_CONTENT_TAGS: Record<RichContentTagType, RichContentTag> = {
  FOLLOWUP: {
    regex: /<f>(.*?)<\/f>/g,
    location: "bottom",
    component: "FollowUpSuggestion",
    description: "Follow-up questions displayed at the bottom of the chat",
  },
  BUTTON: {
    regex: /<b(\s+[^>]*)?>(.*?)<\/b>/g,
    attributeRegex: /(\w+)="([^"]*)"/g,
    location: "inside",
    component: "ButtonElement",
    description: "Interactive buttons inside the chat bubble",
  },
  SCHEDULING: {
    regex: /<scheduling-widget(?:\s+([^>]*?))?>([^<]*)<\/scheduling-widget>/g,
    attributeRegex: /(\w+)="([^"]*)"/g,
    location: "below",
    component: "SchedulingWidget",
    allowedDomains: ["calendly.com"],
    description: "Scheduling buttons displayed below the chat bubble",
  },
  CALENDLY: {
    regex: /<calendly>([^<]+)<\/calendly>/g,
    location: "below",
    component: "SchedulingWidget",
    description: "Calendly scheduling buttons displayed below the chat bubble",
  },
  IFRAME: {
    regex: /<iframe(\s+[^>]*)?>(.*?)<\/iframe>/g,
    attributeRegex: /(\w+)="([^"]*)"/g,
    location: "below",
    component: "IframeContent",
    requiredAttributes: ["src"],
    // For security, limit which domains can be embedded
    allowedDomains: [
      "calendly.com",
      "youtube.com",
      "vimeo.com",
      "google.com",
      "docs.google.com",
      "forms.gle",
      "typeform.com",
    ],
    description: "External content embedded as an iframe below the chat bubble",
  },
  LINK: {
    regex: /<link(\s+[^>]*)?>(.*?)<\/link>/g,
    attributeRegex: /(\w+)="([^"]*)"/g,
    location: "below",
    component: "LinkPreview",
    requiredAttributes: ["url"],
    description: "Rich link previews displayed as cards below the chat bubble",
  },
  HYPERLINK: {
    regex: /<hyperlink(\s+[^>]*)?>(.*?)<\/hyperlink>/g,
    attributeRegex: /(\w+)="([^"]*)"/g,
    location: "inside",
    component: "InlineLink",
    requiredAttributes: ["url"],
    description: "Inline hyperlinks colored with organization color",
  },
};
